import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { formatNumber } from '@/services/format';
import type { ClientSummary, PagedResult } from '@/types';

export function ClientsPage() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [data, setData] = useState<PagedResult<ClientSummary> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setData(await adminApi.clients(page, pageSize));
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Não foi possível carregar os clientes');
      } finally {
        setLoading(false);
      }
    })();
  }, [page, pageSize]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="p-8 max-w-[1280px] mx-auto">
      <h1 className="page-h1 mb-8">Clientes</h1>
      {loading && <p className="text-gray-500">A carregar…</p>}
      {error && <p className="text-magni-red-600">{error}</p>}
      {!loading && !error && data && (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-left">
                <tr>
                  <th className="px-6 py-3 font-semibold">ID do cliente</th>
                  <th className="px-6 py-3 font-semibold text-right">Funcionalidades usadas</th>
                  <th className="px-6 py-3 font-semibold text-right">Eventos</th>
                  <th className="px-6 py-3 font-semibold text-right">Quantidade total</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.clientId} className="border-t border-gray-100">
                    <td className="px-6 py-3 font-mono">
                      <Link to={`/clients/${c.clientId}`} className="text-magni-red hover:underline">{c.clientId}</Link>
                    </td>
                    <td className="px-6 py-3 text-right text-gray-800">{formatNumber(c.featureCount)}</td>
                    <td className="px-6 py-3 text-right text-gray-800">{formatNumber(c.actionCount)}</td>
                    <td className="px-6 py-3 text-right text-gray-800">{formatNumber(c.totalQuantity)}</td>
                  </tr>
                ))}
                {data.items.length === 0 && (
                  <tr><td colSpan={4} className="px-6 py-6 text-center text-gray-500">Sem clientes com utilização.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <p>{formatNumber(data.total)} clientes · página {data.page} de {totalPages}</p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >Anterior</button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                className="px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >Seguinte</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
